import { useState } from "react";
import { Check } from "lucide-react";
import subscriptionApi from "../api/subscriptionApi";
import Btn from "./Btn";
import T from "../theme/colors";

const PLANS = [
    { months: 1, label: "1 Month" },
    { months: 3, label: "3 Months" },
    { months: 6, label: "6 Months" },
    { months: 12, label: "12 Months" },
];

function BreakRow({ label, value, strong }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: strong ? 15 : 13 }}>
            <span style={{ color: strong ? "var(--text-0)" : "var(--text-2)", fontWeight: strong ? 700 : 500 }}>{label}</span>
            <span style={{ color: "var(--text-0)", fontWeight: strong ? 800 : 600 }}>{value}</span>
        </div>
    );
}

/**
 * Phase 36da.99.gj.2 — shared plan picker.  Price = monthly × months.
 * Used by both the user Subscription page and the locked PayWall.
 *
 * Props:
 *   monthlyPaise : number  — the 1-month price (from /status amountPaise)
 *   canPay       : bool    — show the pay button (owner/admin only)
 */
export default function PlanPicker({ monthlyPaise, canPay = true, gstPercent = 18, gstInclusive = false }) {
    const monthly = Number(monthlyPaise) || 0;
    const pct = Number(gstPercent) || 0;
    const [months, setMonths] = useState(1);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const rupees = (paise) => Math.round(paise / 100).toLocaleString("en-IN");
    const base = monthly * months;
    const gst = gstInclusive ? Math.round(base - (base * 100) / (100 + pct)) : Math.round((base * pct) / 100);
    const taxable = gstInclusive ? base - gst : base;
    const total = gstInclusive ? base : base + gst;

    const pay = async () => {
        setErr(null); setBusy(true);
        try {
            const res = await subscriptionApi.pay(months);
            const data = res?.data?.data ?? res?.data;
            if (data?.redirectUrl) { window.location.href = data.redirectUrl; return; }
            setErr("Could not start payment. Please try again."); setBusy(false);
        } catch (e) {
            setErr(e?.response?.data?.message || "Could not start payment. Please try again.");
            setBusy(false);
        }
    };

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginBottom: 18 }}>
                {PLANS.map(p => {
                    const active = months === p.months;
                    return (
                        <button key={p.months} onClick={() => setMonths(p.months)} style={{
                            position: "relative", textAlign: "left", cursor: "pointer",
                            border: `1.5px solid ${active ? "var(--primary)" : "var(--border)"}`,
                            background: active ? "var(--primary-dim)" || "var(--bg-app)" : "var(--bg-card)",
                            borderRadius: 11, padding: "13px 14px", fontFamily: "var(--font-ui)",
                            transition: "all .15s ease",
                        }}>
                            {active && (
                                <span style={{
                                    position: "absolute", top: 9, right: 9, width: 18, height: 18, borderRadius: "50%",
                                    background: "var(--primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                                }}><Check size={12} /></span>
                            )}
                            <div style={{ fontSize: 16, fontWeight: 700, color: active ? "var(--primary)" : "var(--text-0)" }}>{p.label}</div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-0)", marginTop: 6 }}>₹{rupees(monthly * p.months)}</div>
                            <div style={{ fontSize: 13, color: "var(--text-2)", marginTop: 2 }}>₹{rupees(monthly)}/mo</div>
                        </button>
                    );
                })}
            </div>

            {err && (
                <div style={{
                    background: "var(--danger-bg)", color: "var(--danger)", border: `1px solid var(--danger-bdr)`,
                    borderRadius: 8, padding: "9px 12px", fontSize: 15, marginBottom: 14,
                }}>{err}</div>
            )}

            <div style={{
                background: "var(--bg-app)", border: `1px solid var(--border)`, borderRadius: 10,
                padding: "12px 16px", marginBottom: 14,
            }}>
                <BreakRow label="Subtotal (taxable)" value={`₹${rupees(taxable)}`} />
                <BreakRow label={`GST @ ${pct}%${gstInclusive ? " (included)" : ""}`} value={`₹${rupees(gst)}`} />
                <div style={{ height: 1, background: "var(--border)", margin: "8px 0" }} />
                <BreakRow label="Total payable" value={`₹${rupees(total)}`} strong />
            </div>

            {canPay ? (
                <Btn variant="primary" size="lg" loading={busy} onClick={pay} style={{ width: "100%", padding: "13px 0", fontSize: 17 }}>
                    {busy ? "Redirecting to PhonePe…" : `Pay ₹${rupees(total)} with PhonePe`}
                </Btn>
            ) : (
                <div style={{
                    background: "var(--warn-bg)", border: `1px solid var(--warn-bdr)`, color: "var(--warn)",
                    borderRadius: 9, padding: "12px 14px", fontSize: 16, lineHeight: 1.5,
                }}>
                    Only the account owner/administrator can pay. Please ask them to renew.
                </div>
            )}
        </div>
    );
}